import { useParams } from "react-router-dom";
import FormType from "enums/FormType";
import { useEffect, useState } from "react";
import FormEditorContainer from "features/qualityForm/formEditor/FormEditorContainer";

import { loadForm, loadFormNames } from "features/qualityForm/qualityFormSlice";
import {
    getFavoriteTemplates,
    getTemplates,
} from "features/templates/templateSlice";
import { fetchTopics } from "features/topics/topicsSlice";
import { copyFormSliceActions } from "features/qualityForm/formEditor/copyFormSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { NotFound } from "./NotFound";
import { EvaluationFormEvaluationFormTypeEnum } from "@genesys/cloud-frontend/models";
import { selectHasMultipleSelectQuestions } from "features/qualityForm/qualityFormValidations";
import { NotAuthorized } from "./NotAuthorized";
import toastService from "services/toastService";
import {
    selectAutomatedEvaluationFeatureEnabled,
    selectIsAutomatedEvaluationForm,
} from "features/qualityForm/qualityFormSlices";

export function EvaluationForm({
    evaluationFormType,
}: {
    evaluationFormType?: EvaluationFormEvaluationFormTypeEnum;
}) {
    const { evaluationFormId } = useParams();
    const [hasShownToast, setHasShownToast] = useState(false);

    const {
        isAutomatedEvaluationCompletionFeatureEnabled,
        isMultipleSelectFTEnabled,
    } = useAppSelector((state) => ({
        isAutomatedEvaluationCompletionFeatureEnabled:
            selectAutomatedEvaluationFeatureEnabled(state),
        isMultipleSelectFTEnabled: selectFeatureToggle(
            state,
            FeatureToggle.MULTIPLE_SELECT
        ),
    }));

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    const dispatch = useAppDispatch();
    useEffect(() => {
        if (evaluationFormId) {
            // For new forms, set evaluationFormType based on path
            dispatch(
                loadForm({
                    formType: FormType.EVALUATION,
                    formId: evaluationFormId,
                    ...(evaluationFormId === "new"
                        ? { evaluationFormType }
                        : {}),
                })
            );
            dispatch(getTemplates({ isAutomatedEvaluationForm }));
            dispatch(getFavoriteTemplates());
            dispatch(fetchTopics());
            dispatch(loadFormNames(FormType.EVALUATION));
            dispatch(copyFormSliceActions.initializeCopyingViewMode());
        }
    }, [
        dispatch,
        evaluationFormId,
        isAutomatedEvaluationCompletionFeatureEnabled,
        evaluationFormType,
        isAutomatedEvaluationForm,
    ]);

    const hasMultipleSelectQuestion = useAppSelector(
        selectHasMultipleSelectQuestions
    );

    const cannotOpenEvaluationFormWithMSQ =
        !isMultipleSelectFTEnabled && hasMultipleSelectQuestion;

    useEffect(() => {
        if (cannotOpenEvaluationFormWithMSQ && !hasShownToast) {
            toastService.notifyMinorAlert(
                "composer.evaluations.evaluation.failToOpenForm.title",
                {
                    bodyMessageKey:
                        "composer.evaluations.evaluation.failToOpenForm.message",
                }
            );
            setHasShownToast(true);
        }
    }, [cannotOpenEvaluationFormWithMSQ, hasShownToast]);

    // If the feature is disabled AND we do have an evaluationFormType,
    // show 404; otherwise, show the form.
    if (
        !isAutomatedEvaluationCompletionFeatureEnabled &&
        evaluationFormType != null
    ) {
        return <NotFound />;
    }

    // check if the form has multiple select questions?
    if (cannotOpenEvaluationFormWithMSQ) {
        return <NotAuthorized />;
    }

    return <FormEditorContainer formType={FormType.EVALUATION} />;
}
